import React from 'react';
import SimpleInteractiveRenderer from './SimpleInteractiveRenderer';
import { useInteractiveActivity } from '../hooks/useInteractiveActivity';

interface InteractiveActivityProps {
  /** UUID of the InteractiveActivity to load and display */
  activityId: string | null | undefined;
  /** Additional CSS classes */
  className?: string;
  /** Custom styling */
  style?: React.CSSProperties;
  /** Minimum height for the interactive content */
  minHeight?: number;
}

/**
 * InteractiveActivity Component
 *
 * Responsible for:
 * - Loading InteractiveActivity data by ID via API
 * - Passing the content to SimpleInteractiveRenderer for display
 * - Handling loading and error states
 *
 * The SimpleInteractiveRenderer handles the actual content rendering and iframe management.
 * This component focuses purely on data fetching and state management.
 * No submission/answering functionality - just pure display.
 */
const InteractiveActivity: React.FC<InteractiveActivityProps> = ({
  activityId,
  className = '',
  style = {},
  minHeight = 400,
}) => {
  const {
    data: activity,
    error,
    isLoading,
  } = useInteractiveActivity(activityId);

  // Loading state
  if (isLoading) {
    return (
      <div
        className={`interactive-activity-loading ${className}`}
        style={{
          padding: '40px 20px',
          textAlign: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          color: '#666',
          minHeight: minHeight + 'px',
          ...style,
        }}
      >
        <div style={{ marginBottom: '16px' }}>
          <div
            style={{
              width: '40px',
              height: '40px',
              border: '3px solid #f3f3f3',
              borderTop: '3px solid #4CAF50',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }}
          />
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
        Loading interactive content...
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className={`interactive-activity-error ${className}`}
        style={{
          padding: '20px',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          ...style,
        }}
      >
        <strong>Error loading interactive content:</strong>{' '}
        {error.message || 'Failed to load activity'}
        {process.env.NODE_ENV === 'development' && (
          <div
            style={{
              marginTop: '10px',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}
          >
            Activity ID: {activityId}
          </div>
        )}
      </div>
    );
  }

  // No activity ID provided
  if (!activityId) {
    return (
      <div
        className={`interactive-activity-empty ${className}`}
        style={{
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9',
          color: '#666',
          ...style,
        }}
      >
        No interactive activity specified
      </div>
    );
  }

  // No activity found
  if (!activity) {
    return (
      <div
        className={`interactive-activity-not-found ${className}`}
        style={{
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          ...style,
        }}
      >
        Interactive activity not found
        {process.env.NODE_ENV === 'development' && (
          <div
            style={{
              marginTop: '10px',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}
          >
            Activity ID: {activityId}
          </div>
        )}
      </div>
    );
  }

  // Check for content - no content means no display
  if (!activity.s3_url && !activity.html_blob) {
    return (
      <div
        className={`interactive-activity-no-content ${className}`}
        style={{
          padding: '20px',
          textAlign: 'center',
          border: '1px solid #fff3cd',
          borderRadius: '8px',
          backgroundColor: '#ffeaa7',
          color: '#856404',
          minHeight: minHeight + 'px',
          ...style,
        }}
      >
        Interactive activity has no content
        {process.env.NODE_ENV === 'development' && (
          <div
            style={{
              marginTop: '10px',
              fontSize: '12px',
              fontFamily: 'monospace',
            }}
          >
            Activity: {activity.name} ({activity.id})
          </div>
        )}
      </div>
    );
  }

  // Render the interactive content
  return (
    <div className={`interactive-activity ${className}`} style={style}>
      {process.env.NODE_ENV === 'development' && (
        <div
          style={{
            fontSize: '11px',
            color: '#999',
            padding: '4px 8px',
            fontFamily: 'monospace',
          }}
        >
          {activity.name} • {activity.content_source}
        </div>
      )}
      <SimpleInteractiveRenderer
        htmlSrc={activity.s3_url}
        htmlBlob={activity.html_blob}
        minHeight={minHeight}
      />
    </div>
  );
};

export default InteractiveActivity;
